import { memo } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Heading, PageNavigator, VStack } from '@library';
import SpinnerPage from '../../core/components/pages/SpinnerPage';
import styles from './MotorcycleSearch.module.scss';

interface VehicleSearchTableProps {
    loading: boolean;
    vehiclesTable: ReactNode;
    filteredVehicles: unknown[];
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    showTopPagination?: boolean;
    totalVehiclesCount?: number;
}

const VehicleSearchTable = memo(
    ({
        loading,
        vehiclesTable,
        filteredVehicles,
        pageSize,
        page,
        setPage,
        showTopPagination = true,
        totalVehiclesCount,
    }: VehicleSearchTableProps) => {
        if (loading) {
            return <SpinnerPage />;
        }

        if (filteredVehicles.length === 0) {
            return <FormattedMessage id="no results" />;
        }

        const pageCount = Math.ceil(
            (totalVehiclesCount ?? filteredVehicles.length) / pageSize
        );

        return (
            <VStack gap="small" align="end">
                {filteredVehicles.length > pageSize && showTopPagination && (
                    <PageNavigator
                        autoScroll
                        count={pageCount}
                        page={page}
                        onChange={setPage}
                    />
                )}
                {vehiclesTable}
                <PageNavigator
                    autoScroll
                    count={pageCount}
                    page={page}
                    onChange={setPage}
                />
            </VStack>
        );
    }
);

const VehicleSearch = ({
    vehicleFilters,
    title,
    ...props
}: {
    vehicleFilters: ReactNode;
    vehiclesTable: ReactNode;
    title: string;
} & VehicleSearchTableProps) => {
    return (
        <VStack gap="medium">
            <VStack gap="small">
                <div className={styles.label}>
                    <Heading h3>
                        <FormattedMessage id={title} />
                    </Heading>
                </div>
                {vehicleFilters}
            </VStack>
            <VehicleSearchTable {...props} />
        </VStack>
    );
};

export default memo(VehicleSearch);
